import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './AdminSchedule.css';
import ClassDetails from '../schedule/ClassDetails';

const AdminSchedule = () => {
  const { t, i18n } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  // State for class details modal
  const [showClassDetails, setShowClassDetails] = useState(false);
  const [classDetailsData, setClassDetailsData] = useState(null);

  // State for participant management
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [showRemoveParticipantModal, setShowRemoveParticipantModal] = useState(false);
  const [showMissedClassModal, setShowMissedClassModal] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [selectedClassForParticipants, setSelectedClassForParticipants] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [missedClassParticipants, setMissedClassParticipants] = useState([]);

  // Force re-render on language change
  useEffect(() => {
    // This empty effect depends on i18n.language
    // and will trigger a re-render when language changes
  }, [i18n.language]);

  // Format date to YYYY-MM-DD string
  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Parse date string to Date object
  const parseDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Check if a date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Fetch available dates (dates with classes) for the current month
  useEffect(() => {
    const fetchAvailableDates = async () => {
      try {
        console.log(`Fetching available dates for month: ${currentMonth}/${currentYear}`);
        
        const response = await axios.get(`classes/available-dates`, {
          params: {
            month: currentMonth,
            year: currentYear
          }
        });
        
        console.log('Available dates response:', response.data);
        
        // Parse the date strings to Date objects
        if (response.data && Array.isArray(response.data.dates_with_classes)) {
          const dates = response.data.dates_with_classes.map(dateStr => parseDate(dateStr));
          setAvailableDates(dates);
        } else {
          console.error('Unexpected format for available dates:', response.data);
          setAvailableDates([]);
        }
      } catch (err) {
        console.error('Error fetching available dates:', err);
        setAvailableDates([]);
      }
    };

    fetchAvailableDates();
  }, [currentMonth, currentYear]);

  // Fetch classes for the selected date
  useEffect(() => {
    fetchClasses(selectedDate);
  }, [selectedDate]);

  // Helper function to check if a date is available (has classes)
  const isDateAvailable = (date) => {
    return availableDates.some(availableDate => 
      availableDate.getDate() === date.getDate() &&
      availableDate.getMonth() === date.getMonth() &&
      availableDate.getFullYear() === date.getFullYear()
    );
  };

  // Generate calendar days for the current month
  const generateCalendarDays = () => {
    const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const firstDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    // Add cells for each day of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentYear, currentMonth - 1, i);
      const isAvailable = isDateAvailable(date);
      const isSelected = selectedDate && 
        date.getDate() === selectedDate.getDate() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getFullYear() === selectedDate.getFullYear();
      
      days.push(
        <div 
          key={`day-${i}`} 
          className={`calendar-day ${isAvailable ? 'available' : ''} ${isSelected ? 'selected' : ''} ${isToday(date) ? 'today' : ''}`}
          onClick={() => setSelectedDate(date)}
        >
          {i}
        </div>
      );
    }
    
    return days;
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  // Navigate to next month
  const goToNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Go to today's date
  const goToToday = () => {
    const today = new Date();
    setSelectedDate(today);
    setCurrentMonth(today.getMonth() + 1);
    setCurrentYear(today.getFullYear());
  };

  // Get the month name
  const getMonthName = (month) => {
    return t(`${t(`month_${month}`, t(`${[
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ][month - 1]}`))}`);
  };

  // Format time (e.g., "14:00" -> "2:00 PM")
  const formatTime = (timeStr) => {
    try {
      if (!timeStr) return 'N/A';
      
      const [hours, minutes] = timeStr.split(':').map(Number);
      
      // Check if we have valid numbers
      if (isNaN(hours) || isNaN(minutes)) return timeStr;
      
      const period = hours >= 12 ? 'PM' : 'AM';
      const formattedHours = hours % 12 || 12;
      return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeStr || 'N/A';
    }
  };

  // Get difficulty text based on numerical value
  const getDifficultyText = (difficulty) => {
    if (!difficulty) return t('normal');
    
    // Convert to number if it's a string
    const difficultyNum = parseInt(difficulty, 10);
    
    switch(difficultyNum) {
      case 1:
        return t('oddych');
      case 2:
        return t('normal');
      case 3:
        return t('makacka');
      default:
        return t('normal');
    }
  };

  // Get difficulty class
  const getDifficultyClass = (difficulty) => {
    if (!difficulty) return 'difficulty-unknown';
    
    // Convert to number if it's a string
    const difficultyNum = parseInt(difficulty, 10);
    
    switch(difficultyNum) {
      case 1:
        return 'difficulty-beginner';
      case 2:
        return 'difficulty-intermediate';
      case 3:
        return 'difficulty-advanced';
      default:
        return 'difficulty-unknown';
    }
  };

  // Format date for display (e.g., "Monday, January 1, 2023")
  const formatDateForDisplay = (date) => {
    const dayIndex = date.getDay();
    const monthIndex = date.getMonth();
    
    const dayName = t([
      'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
    ][dayIndex]);
    
    const monthName = t([
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ][monthIndex]);
    
    const day = date.getDate();
    const year = date.getFullYear();
    
    return `${dayName}, ${monthName} ${day}, ${year}`;
  };

  // Update the fetchClasses function to be reusable
  const fetchClasses = async (date) => {
    setLoading(true);
    setError(null);
    
    try {
      const dateStr = formatDateToString(date);
      console.log(`Admin Schedule: Fetching classes for date: ${dateStr}`);
      
      const response = await axios.get(`classes/by-date`, {
        params: {
          date: dateStr
        }
      });
      
      console.log('Admin Schedule: API response:', response.data);
      
      if (response.data && Array.isArray(response.data.classes)) {
        // Additional check to ensure we're only displaying classes for the selected date
        const selectedDateStr = formatDateToString(date);
        const filteredClasses = response.data.classes.filter(yogaClass => {
          // If the class has a date property, use it for filtering
          if (yogaClass.date) {
            return yogaClass.date === selectedDateStr;
          }
          // Otherwise assume it's for the correct date (as filtered by backend)
          return true;
        });
        
        console.log(`Admin Schedule: Filtered ${response.data.classes.length} to ${filteredClasses.length} classes for date ${selectedDateStr}`);
        setClasses(filteredClasses);
      } else {
        console.error('Admin Schedule: Unexpected response format:', response.data);
        setClasses([]);
        setError(t('failed_load_classes'));
      }
    } catch (err) {
      console.error('Admin Schedule: Error fetching classes:', err);
      setError(t('failed_load_classes_try_again'));
      setClasses([]);
    } finally {
      setLoading(false);
    }
  };

  // Add this helper function
  const isClassStarted = (startTime) => {
    const classTime = new Date(selectedDate);
    const [hours, minutes] = startTime.split(':');
    classTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0);
    const now = new Date();
    return now >= classTime;
  };

  // Add this handler for showing class details
  const handleShowDetails = (yogaClass) => {
    setClassDetailsData(yogaClass);
    setShowClassDetails(true);
  };

  // Add this handler for closing class details
  const handleCloseClassDetails = () => {
    setShowClassDetails(false);
    setClassDetailsData(null);
  };

  // Admin-specific functions for participant management
  const handleAddParticipant = (yogaClass) => {
    setSelectedClassForParticipants(yogaClass);
    setShowAddParticipantModal(true);
    fetchCurrentParticipants(yogaClass.id);
    fetchAllUsers();
  };

  const handleRemoveParticipant = (yogaClass) => {
    setSelectedClassForParticipants(yogaClass);
    setShowRemoveParticipantModal(true);
    fetchCurrentParticipants(yogaClass.id);
  };

  const handleMissedClassReport = (yogaClass) => {
    setSelectedClassForParticipants(yogaClass);
    setShowMissedClassModal(true);
    fetchCurrentParticipants(yogaClass.id);
  };

  const handleShowParticipants = (yogaClass) => {
    setSelectedClassForParticipants(yogaClass);
    setShowParticipantsModal(true);
    fetchCurrentParticipants(yogaClass.id);
  };

  const fetchCurrentParticipants = async (classId) => {
    setLoadingParticipants(true);
    try {
      const response = await axios.get(`admin/classes/${classId}/participants`);
      if (response.data && Array.isArray(response.data.participants)) {
        setParticipants(response.data.participants);
        setMissedClassParticipants(response.data.participants);
      } else {
        console.error('Unexpected format for participants:', response.data);
        setParticipants([]);
        setMissedClassParticipants([]);
      }
    } catch (err) {
      console.error('Error fetching participants:', err);
      setParticipants([]);
      setMissedClassParticipants([]);
    } finally {
      setLoadingParticipants(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get('admin/users');
      if (response.data && Array.isArray(response.data.users)) {
        setAllUsers(response.data.users);
      } else {
        console.error('Unexpected format for users:', response.data);
        setAllUsers([]);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setAllUsers([]);
    }
  };

  const handleAddUserToClass = async (userId) => {
    if (!selectedClassForParticipants) return;
    
    try {
      await axios.post(`admin/bookings`, {
        user_id: userId,
        class_id: selectedClassForParticipants.id
      });
      
      // Refresh participants list
      fetchCurrentParticipants(selectedClassForParticipants.id);
      // Refresh classes to update available spots
      fetchClasses(selectedDate);
    } catch (err) {
      console.error('Error adding user to class:', err);
      alert('Failed to add user to class. Please try again.');
    }
  };

  const handleRemoveUserFromClass = async (userId) => {
    if (!selectedClassForParticipants) return;
    
    try {
      await axios.delete(`admin/bookings`, {
        data: {
          user_id: userId,
          class_id: selectedClassForParticipants.id
        }
      });
      
      // Refresh participants list
      fetchCurrentParticipants(selectedClassForParticipants.id);
      // Refresh classes to update available spots
      fetchClasses(selectedDate);
    } catch (err) {
      console.error('Error removing user from class:', err);
      alert('Failed to remove user from class. Please try again.');
    }
  };

  const closeParticipantModals = () => {
    setShowAddParticipantModal(false);
    setShowRemoveParticipantModal(false);
    setShowMissedClassModal(false);
    setShowParticipantsModal(false);
    setSelectedClassForParticipants(null);
    setSearchTerm('');
  };

  // Filter users based on search term
  const filteredUsers = allUsers.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if a user is already a participant
  const isUserParticipant = (userId) => {
    return participants.some(participant => participant.user_id === userId);
  };

  // Add this function to handle missed class count adjustment
  const handleMissedClassAdjustment = async (userId, increment) => {
    try {
      // Call the appropriate endpoint based on whether we're incrementing or decrementing
      const endpoint = increment 
        ? 'admin/missed-class/increment' 
        : 'admin/missed-class/decrement';
      
      const response = await axios.post(endpoint, { user_id: userId });
      
      if (response.data && response.data.success) {
        // Update the UI with the new missed class count
        setMissedClassParticipants(prevParticipants => 
          prevParticipants.map(participant => {
            if (participant.user_id === userId) {
              return { 
                ...participant, 
                missed_class: response.data.user.missed_class 
              };
            }
            return participant;
          })
        );

        // Also write attendance_status to the booking so it shows in the Excel report
        if (selectedClassForParticipants?.id) {
          const attendanceStatus = increment ? 'no-show' : 'attended';
          try {
            await axios.put(`classes/${selectedClassForParticipants.id}/attendance`, {
              attendance: [{ user_id: userId, status: attendanceStatus }]
            });
          } catch (attendanceErr) {
            // Non-blocking — missed class counter already updated, log silently
            console.error('Failed to update attendance_status:', attendanceErr);
          }
        }
      } else {
        console.error('Error adjusting missed class count:', response.data?.error || 'Unknown error');
        alert(t('failed_to_adjust_missed_class'));
      }
    } catch (err) {
      console.error('Error adjusting missed class count:', err);
      alert(t('failed_to_adjust_missed_class'));
    }
  };

  return (
    <div className="admin-schedule-container">
      <h1>{t('admin_class_schedule')}</h1>
      
      <div className="schedule-content">
        <div className="schedule-calendar-section">
          <div className="calendar-controls">
            <button onClick={goToPreviousMonth} className="calendar-control-btn">&laquo;</button>
            <h2>{getMonthName(currentMonth)} {currentYear}</h2>
            <button onClick={goToNextMonth} className="calendar-control-btn">&raquo;</button>
            <button onClick={goToToday} className="today-btn">{t('today')}</button>
          </div>
          
          <div className="calendar">
            <div className="calendar-header">
              <div>{t('sun')}</div>
              <div>{t('mon')}</div>
              <div>{t('tue')}</div>
              <div>{t('wed')}</div>
              <div>{t('thu')}</div>
              <div>{t('fri')}</div>
              <div>{t('sat')}</div>
            </div>
            <div className="calendar-grid">
              {generateCalendarDays()}
            </div>
          </div>
        </div>
        
        <div className="schedule-classes-section">
          <div className="classes-container">
            <div className="selected-date-info">
              <h3>{selectedDate ? formatDateForDisplay(selectedDate) : ''}</h3>
            </div>
            {error ? (
              <div className="error">{error}</div>
            ) : loading ? (
              <div className="loading">{t('loading_classes')}</div>
            ) : classes.length > 0 ? (
              classes.map((yogaClass, index) => (
                <div key={yogaClass.id || index} className="class-card">
                  <div className="class-image">
                    <img 
                      src={yogaClass.instructor_image || '/images/defaults/instructor-placeholder.jpg'} 
                      alt={yogaClass.instructor || t('staff')} 
                    />
                  </div>
                  
                  <div className="class-content">
                    <h4 className="class-title">
                      {yogaClass.title || t('untitled_class')} 
                    </h4>
                    
                    <div className="class-time-range">
                      {formatTime(yogaClass.start_time)} - {formatTime(yogaClass.end_time)}
                    </div>
                    
                    <div className="instructor-name">
                      {yogaClass.instructor || t('staff')}
                    </div>
                    
                    <button className="show-details-button" onClick={() => handleShowDetails(yogaClass)}>
                      {t('show_details')} <span className="arrow">&raquo;</span>
                    </button>
                  </div>
                  
                  <div className="class-actions">
                    <div className="class-price">
                      {yogaClass.price !== undefined ? yogaClass.price : 0} &euro;
                    </div>
                    
                    <div className="admin-buttons">
                      <button
                        className="admin-button add-participant"
                        onClick={() => handleAddParticipant(yogaClass)}
                      >
                        {t('add_participant')}
                      </button>
                      
                      <button
                        className="admin-button remove-participant"
                        onClick={() => handleRemoveParticipant(yogaClass)}
                        disabled={yogaClass.available_spots >= yogaClass.max_capacity}
                      >
                        {t('remove_participant')}
                      </button>

                      <button
                        className="admin-button missed-class-report"
                        onClick={() => handleMissedClassReport(yogaClass)}
                      >
                        {t('report_missed_class')}
                      </button>

                      <button
                        className="admin-button show-participants"
                        onClick={() => handleShowParticipants(yogaClass)}
                      >
                        {t('show_participants')}
                      </button>
                    </div>
                    
                    <div className="class-capacity">
                      <div className="capacity-bar">
                        <div 
                          className="capacity-filled" 
                          style={{ 
                            width: `${Math.min(100, Math.max(0, 
                              ((yogaClass.max_capacity || 10) - (yogaClass.available_spots || 0)) / 
                              (yogaClass.max_capacity || 10) * 100
                            ))}%` 
                          }}
                        ></div>
                      </div>
                     
                      <span>{t('free_spots')}: {yogaClass.available_spots || 0}</span>
                      <span className={`difficulty-tag ${getDifficultyClass(yogaClass.difficulty)}`}>
                        {getDifficultyText(yogaClass.difficulty)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-classes">
                <p>{t('no_classes_for_date')}</p>
                <p>{t('try_another_date')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Add Participant Modal */}
      {showAddParticipantModal && selectedClassForParticipants && (
        <div className="modal-overlay">
          <div className="admin-modal">
            <div className="modal-header">
              <h3>{t('add_participant_to')} {selectedClassForParticipants.title || t('class')}</h3>
              <button className="close-button" onClick={closeParticipantModals}>&times;</button>
            </div>
            
            <div className="modal-content">
              <div className="current-participants">
                <h4>{t('current_participants')}</h4>
                {loadingParticipants ? (
                  <div className="loading">{t('loading_participants')}</div>
                ) : participants.length > 0 ? (
                  <ul className="participants-list">
                    {participants.map(participant => (
                      <li key={participant.user_id}>
                        <div className="participant-info">
                          {participant.name} ({participant.email})
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>{t('no_participants_yet')}</p>
                )}
              </div>
              
              <div className="search-users">
                <h4>{t('add_new_participant')}</h4>
                <div className="search-box">
                  <input
                    type="text"
                    placeholder={t('search_users')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                
                <div className="user-results">
                  {searchTerm ? (
                    filteredUsers.length > 0 ? (
                      <ul className="user-list">
                        {filteredUsers.map(user => (
                          <li key={user.id}>
                            <div className="user-info">
                              {user.name} ({user.email})
                            </div>
                            <button
                              className={`add-user-button ${isUserParticipant(user.id) ? 'disabled' : ''}`}
                              onClick={() => handleAddUserToClass(user.id)}
                              disabled={isUserParticipant(user.id)}
                            >
                              {isUserParticipant(user.id) ? t('already_booked') : t('add')}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>{t('no_users_found')} "{searchTerm}"</p>
                    )
                  ) : (
                    <p>{t('type_to_search')}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Remove Participant Modal */}
      {showRemoveParticipantModal && selectedClassForParticipants && (
        <div className="modal-overlay">
          <div className="admin-modal">
            <div className="modal-header">
              <h3>{t('remove_participant_from')} {selectedClassForParticipants.title || t('class')}</h3>
              <button className="close-button" onClick={closeParticipantModals}>&times;</button>
            </div>
            
            <div className="modal-content">
              {loadingParticipants ? (
                <div className="loading">{t('loading_participants')}</div>
              ) : participants.length > 0 ? (
                <ul className="participants-list">
                  {participants.map(participant => (
                    <li key={participant.user_id}>
                      <div className="participant-info">
                        {participant.name} ({participant.email})
                      </div>
                      <button
                        className="remove-user-button"
                        onClick={() => handleRemoveUserFromClass(participant.user_id)}
                      >
                        {t('remove')}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>{t('no_participants')}</p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Missed Class Report Modal */}
      {showMissedClassModal && selectedClassForParticipants && (
        <div className="modal-overlay">
          <div className="admin-modal">
            <div className="modal-header">
              <h3>{t('report_missed_class_for')} {selectedClassForParticipants.title || t('class')}</h3>
              <button className="close-button" onClick={closeParticipantModals}>&times;</button>
            </div>
            
            <div className="modal-content">
              {loadingParticipants ? (
                <div className="loading">{t('loading_participants')}</div>
              ) : missedClassParticipants.length > 0 ? (
                <ul className="participants-list">
                  {missedClassParticipants.map(participant => (
                    <li key={participant.user_id}>
                      <div className="participant-info">
                        {participant.name} ({participant.email})
                      </div>
                      <div className="missed-class-count">
                        {t('missed_classes')}: {participant.missed_class || 0}
                        <button 
                          className="adjust-missed-class-button" 
                          onClick={() => handleMissedClassAdjustment(participant.user_id, true)}
                        >
                          +
                        </button>
                        <button 
                          className="adjust-missed-class-button" 
                          onClick={() => handleMissedClassAdjustment(participant.user_id, false)}
                        >
                          -
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>{t('no_missed_classes_to_report')}</p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Show Participants Modal */}
      {showParticipantsModal && selectedClassForParticipants && (
        <div className="modal-overlay">
          <div className="admin-modal participants-display-modal">
            <div className="modal-header">
              <h3>{t('participants_for')} {selectedClassForParticipants.title || t('class')} ({participants.length})</h3>
              <button className="close-button" onClick={closeParticipantModals}>&times;</button>
            </div>
            
            <div className="modal-content">
              {loadingParticipants ? (
                <div className="loading">{t('loading_participants')}</div>
              ) : participants.length > 0 ? (
                <div className="participants-display-grid">
                  {participants.map((participant, index) => (
                    <div key={participant.user_id} className="participant-display-item">
                      <span className="participant-number">{index + 1}.</span>
                      <span className="participant-name">{participant.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-participants-message">{t('no_participants_yet')}</p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Class Details Modal */}
      {showClassDetails && classDetailsData && (
        <ClassDetails 
          yogaClass={classDetailsData}
          onClose={handleCloseClassDetails}
        />
      )}
    </div>
  );
};

export default AdminSchedule; 